import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Switch,
} from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import PhotoLibraryOutlined from "@mui/icons-material/PhotoLibraryOutlined";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import WaterDropOutlined from "@mui/icons-material/WaterDropOutlined";
import FavoriteBorder from "@mui/icons-material/FavoriteBorder";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SignpostOutlined from "@mui/icons-material/SignpostOutlined";
import apiService from "../../../service/Api"; // ✅ API service
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";

const Row = ({
  icon,
  title,
  checked,
  onToggle,
  reduxTheme,
}: {
  icon: React.ReactNode;
  title: string;
  checked: boolean;
  onToggle: () => void;
  reduxTheme: any;
}) => (
  <Paper sx={{ p: 2, height: 70, borderRadius: 2, width: 290 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 42,
            height: 42,
            borderRadius: "50%",
            backgroundColor: reduxTheme.primary_colors?.[800],
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Switch checked={checked} onChange={onToggle} />
    </Box>
  </Paper>
);

const FeaturesPanel = ({
  reduxTheme,
  deceasedId, // ✅ now passed in as prop
  featuresEnabled,
  toggleFeature,
}: {
  reduxTheme: any;
  deceasedId: string | number | null; // ✅ new prop
  featuresEnabled: Record<string, boolean>;
  toggleFeature: (id: string) => void;
}) => {
  const [websiteId, setWebsiteId] = useState<number | null>(null);
  const [features, setFeatures] = useState({
    obituary: false,
    gallery: false,
    livestream: false,
    favourites: false,
    chema: false,
    memories: false,
    events: false,
  });

  // ✅ uses deceasedId from props
  const fetchWebsite = async () => {
    if (!deceasedId) return;

    try {
      const res = await apiService.get(
        `/api/client-portal/websites/?deceased=${deceasedId}`
      );
      const dataResponse = res.data;

      let data: any = null;

      if (Array.isArray(dataResponse) && dataResponse.length > 0) {
        data = dataResponse[0];
      } else if (
        typeof dataResponse === "object" &&
        dataResponse !== null &&
        "results" in dataResponse &&
        Array.isArray((dataResponse as any).results) &&
        (dataResponse as any).results.length > 0
      ) {
        data = (dataResponse as any).results[0];
      } else if (
        typeof dataResponse === "object" &&
        dataResponse !== null &&
        "id" in dataResponse
      ) {
        data = dataResponse;
      }

      if (data?.id) {
        setWebsiteId(data.id);
        setFeatures({
          obituary: data.activate_feature_obituary,
          gallery: data.activate_feature_gallery,
          livestream: data.activate_feature_livestream,
          favourites: data.activate_feature_favourites,
          chema: data.activate_feature_chema,
          memories: data.activate_feature_memories,
          events: data.activate_feature_events,
        });
      }
    } catch (err) {
      console.error("Failed to fetch website info", err);
    }
  };

  useEffect(() => {
    fetchWebsite();
  }, [deceasedId]);

  const handleToggleFeature = async (
    feature:
      | "obituary"
      | "gallery"
      | "livestream"
      | "favourites"
      | "chema"
      | "memories"
      | "events"
  ) => {
    if (!websiteId) return;
    const newValue = !features[feature];
    try {
      await apiService.patch(`/api/client-portal/websites/${websiteId}/`, {
        [`activate_feature_${feature}`]: newValue,
      });
      setFeatures((prev) => ({ ...prev, [feature]: newValue }));
      showSuccessToast?.(`Updated ${feature} successfully.`);
    } catch (err) {
      console.error(`Failed to update ${feature}`, err);
      showErrorToast?.(`Failed to update ${feature}.`);
    }
  };

  const handleToggle = (id: string) => {
    toggleFeature(id);
    if (id === "obituary") handleToggleFeature("obituary");
    if (id === "gallery") handleToggleFeature("gallery");
    if (id === "livestream") handleToggleFeature("livestream");
    if (id === "favourites") handleToggleFeature("favourites");
    if (id === "chema") handleToggleFeature("chema");
    if (id === "memories") handleToggleFeature("memories");
    if (id === "events") handleToggleFeature("events");
  };

  return (
    <Card
      sx={{
        height: 482,
        backgroundColor: reduxTheme.primary_colors?.[800],
        width: 660,
        pt: 6,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, height: "100%", overflow: "hidden" }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
          Choose components to display
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{ overflow: "hidden" }}
        >
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
            <Row
              icon={
                <DescriptionOutlined
                  sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 18 }}
                />
              }
              title="Orbituary"
              checked={features["obituary"]}
              onToggle={() => handleToggle("obituary")}
              reduxTheme={reduxTheme}
            />
            <Row
              icon={
                <PhotoLibraryOutlined
                  sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 18 }}
                />
              }
              title="Gallery"
              checked={features["gallery"]}
              onToggle={() => handleToggle("gallery")}
              reduxTheme={reduxTheme}
            />
            <Row
              icon={
                <PlayCircleOutline
                  sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 18 }}
                />
              }
              title="Livestream"
              checked={features["livestream"]}
              onToggle={() => handleToggle("livestream")}
              reduxTheme={reduxTheme}
            />
            <Row
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: 1,
                    border: `1px solid ${reduxTheme.primary_colors?.[400]}`,
                  }}
                >
                  <WaterDropOutlined
                    sx={{
                      color: reduxTheme.primary_colors?.[400],
                      fontSize: 14,
                    }}
                  />
                </Box>
              }
              title="CHEMA"
              checked={features["chema"]}
              onToggle={() => handleToggle("chema")}
              reduxTheme={reduxTheme}
            />
            <Row
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: `1px solid ${reduxTheme.primary_colors?.[400]}`,
                  }}
                >
                  <FavoriteBorder
                    sx={{
                      color: reduxTheme.primary_colors?.[400],
                      fontSize: 14,
                    }}
                  />
                </Box>
              }
              title="Favourites"
              checked={features["favourites"]}
              onToggle={() => handleToggle("favourites")}
              reduxTheme={reduxTheme}
            />
            <Row
              icon={
                <CalendarMonthIcon
                  sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 18 }}
                />
              }
              title="Memories"
              checked={features["memories"]}
              onToggle={() => handleToggle("memories")}
              reduxTheme={reduxTheme}
            />
            <Row
              icon={
                <SignpostOutlined
                  sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 18 }}
                />
              }
              title="Events"
              checked={features["events"]}
              onToggle={() => handleToggle("events")}
              reduxTheme={reduxTheme}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FeaturesPanel;
